import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eraser, RefreshCw, ShieldAlert, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState, PageHeader, StatusPill } from "@/components/uiext";
import { FirewallStatusCell, clearFirewallStatusCache } from "@/components/FirewallStatusCell";
import { useAuth } from "@/lib/auth.jsx";
import { toast } from "sonner";

const TYPE_KIND = {
  auth_fail: "danger",
  ip_auth_fail: "danger",
  domain_reject: "warn",
  domain_mismatch: "warn",
  quota_block: "info",
  cps_exceeded: "info",
  acl_block: "security",
  ip_locked: "security",
};

export default function SecurityPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canPurge = ["owner", "operator"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [type, setType] = useState("");
  const [summary, setSummary] = useState({ counts: [] });
  const [hideBeforeTs, setHideBeforeTs] = useState(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeDays, setPurgeDays] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = () => {
    const qs = type ? `?event_type=${type}` : "";
    api.get(`/security/events${qs}`).then((r) => setItems(r.data));
    api.get("/security/summary").then((r) => setSummary(r.data));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);
  useEffect(() => {
    const h = setInterval(load, 5000);
    return () => clearInterval(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const visibleItems = useMemo(() => {
    if (!hideBeforeTs) return items;
    return items.filter((e) => (e.ts || "") >= hideBeforeTs);
  }, [items, hideBeforeTs]);

  const clearScreen = () => {
    setHideBeforeTs(new Date().toISOString());
    toast.success(t("pages.security.cleared", "Screen cleared. New events will appear from now on."));
  };
  const restoreScreen = () => setHideBeforeTs(null);

  const doPurge = async () => {
    setBusy(true);
    try {
      const days = Number(purgeDays) || 0;
      const r = await api.delete(`/security/events?older_than_days=${days}`);
      const n = r.data?.deleted ?? 0;
      toast.success(
        days
          ? t("pages.security.purgedOlder", "Purged {{n}} events older than {{d}} days.", { n, d: days })
          : t("pages.security.purgedAll", "Purged {{n}} events (entire log wiped).", { n })
      );
      setPurgeOpen(false);
      setHideBeforeTs(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed"));
    } finally {
      setBusy(false);
    }
  };

  const PURGE_PRESETS = [
    { days: 0,  label: t("pages.security.purge.everything", "Everything"),
      desc: t("pages.security.purge.everythingDesc", "Wipe my entire security log.") },
    { days: 7,  label: t("pages.security.purge.7d", "Older than 7 days"),
      desc: t("pages.security.purge.7dDesc", "Keep only the most recent week.") },
    { days: 30, label: t("pages.security.purge.30d", "Older than 30 days"),
      desc: t("pages.security.purge.30dDesc", "Keep only the most recent month.") },
    { days: 90, label: t("pages.security.purge.90d", "Older than 90 days"),
      desc: t("pages.security.purge.90dDesc", "Keep the last quarter.") },
  ];

  return (
    <div data-testid="page-security">
      <PageHeader
        title={t("pages.security.title")}
        subtitle={t("pages.security.subtitle")}
        icon={ShieldAlert}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" onClick={load} data-testid="sec-refresh">
              <RefreshCw className="h-4 w-4 mr-1" />
              {t("common.refresh", "Refresh")}
            </Button>
            {hideBeforeTs ? (
              <Button variant="outline" size="sm" onClick={restoreScreen} data-testid="sec-restore-screen">
                <Eraser className="h-3.5 w-3.5 mr-1" />
                {t("pages.security.restoreScreen", "Restore screen")}
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={clearScreen} data-testid="sec-clear-screen">
                <Eraser className="h-3.5 w-3.5 mr-1" />
                {t("pages.security.clearScreen", "Clear screen")}
              </Button>
            )}
            {canPurge && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setPurgeOpen(true)}
                data-testid="sec-purge-log"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                {t("pages.security.purgeLog", "Purge log…")}
              </Button>
            )}
          </div>
        }
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {summary.counts.map((c) => (
          <div
            key={c.type}
            className="rounded-lg border border-border bg-card px-4 py-3"
            data-testid={`sec-summary-${c.type}`}
          >
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {c.type}
            </div>
            <div className="text-2xl font-semibold tabular-nums">{c.count}</div>
          </div>
        ))}
        {summary.counts.length === 0 && (
          <div className="col-span-4 text-xs text-muted-foreground">
            {t("pages.security.empty24h", "No events in the last 24 hours.")}
          </div>
        )}
      </div>
      <div className="flex items-end gap-3 mb-3">
        <div>
          <Label className="text-xs">
            {t("pages.security.filterByType", "Filter by type")}
          </Label>
          <Input
            className="w-[220px]"
            placeholder="e.g. auth_fail"
            value={type}
            onChange={(e) => setType(e.target.value)}
          />
        </div>
      </div>

      {hideBeforeTs && (
        <div
          className="mb-3 rounded-md border border-[hsl(var(--info))]/30 bg-[hsl(var(--info))]/5 px-3 py-2 text-[11px]"
          data-testid="sec-hidden-banner"
        >
          {t(
            "pages.security.hiddenBanner",
            "Showing only events newer than {{ts}}.",
            { ts: new Date(hideBeforeTs).toLocaleString() }
          )}{" "}
          <button className="underline text-[hsl(var(--info))]" onClick={restoreScreen}>
            {t("pages.security.restoreLink", "Restore full history")}
          </button>
        </div>
      )}

      {visibleItems.length === 0 ? (
        <EmptyState
          title={items.length === 0 ? "No security events" : t("pages.security.cleared", "Screen cleared")}
          description={
            items.length === 0
              ? t("pages.security.emptyDesc", "When the engine rejects a request (bad domain, bad auth, quota hit, etc.), it lands here.")
              : t("pages.security.clearedDesc", "All currently-known events were timestamped before the screen was cleared.")
          }
        />
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader className="sticky-table-head">
              <TableRow>
                <TableHead className="w-[160px]">{t("common.ts", "Time")}</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>{t("pages.security.sourceIp", "Source IP")}</TableHead>
                <TableHead className="min-w-[220px]">
                  {t("firewall.cell.header", "Firewall")}
                </TableHead>
                <TableHead>{t("pages.security.detail", "Detail")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleItems.map((e, i) => (
                <TableRow key={`${e.ts}-${i}`} className="row-hover" data-testid="security-row">
                  <TableCell className="text-xs">{new Date(e.ts).toLocaleString()}</TableCell>
                  <TableCell>
                    <StatusPill kind={TYPE_KIND[e.type] || "security"}>{e.type}</StatusPill>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{e.ip}</TableCell>
                  <TableCell>
                    {e.ip ? (
                      <FirewallStatusCell
                        ip={e.ip}
                        scope="tenant"
                        onQuickBan={() => {
                          clearFirewallStatusCache(e.ip);
                          load();
                        }}
                      />
                    ) : (
                      <span className="text-[10px] text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-[11px] text-muted-foreground">
                    {JSON.stringify(e.detail)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={purgeOpen} onOpenChange={setPurgeOpen}>
        <DialogContent className="max-w-md" data-testid="sec-purge-dialog">
          <DialogHeader>
            <DialogTitle>{t("pages.security.purgeTitle", "Purge security log")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p className="text-muted-foreground">
              {t(
                "pages.security.purgeWarn",
                "This permanently deletes events from the database. The action is recorded in the Audit Log."
              )}
            </p>
            <div className="space-y-1.5">
              {PURGE_PRESETS.map((p) => (
                <label
                  key={p.days}
                  className={`flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer ${
                    purgeDays === p.days
                      ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10"
                      : "border-border hover:bg-muted/40"
                  }`}
                  data-testid={`sec-purge-preset-${p.days}`}
                >
                  <input
                    type="radio"
                    name="sec-purge-preset"
                    className="mt-0.5 accent-[hsl(var(--accent))]"
                    checked={purgeDays === p.days}
                    onChange={() => setPurgeDays(p.days)}
                  />
                  <span>
                    <span className="font-medium">{p.label}</span>
                    <span className="block text-[11px] text-muted-foreground">{p.desc}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPurgeOpen(false)} disabled={busy}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={doPurge}
              disabled={busy}
              data-testid="sec-purge-confirm"
            >
              {busy ? t("common.busy", "Working…") : t("pages.security.purgeLog", "Purge log")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
